import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

export const LEGAL_ACCEPTANCE_QUERY_KEY = ["legal-acceptance"] as const;

export type AcceptanceStatusResponse = components["schemas"]["AcceptanceStatusResponse"];
export type DocumentAcceptanceResponse = components["schemas"]["DocumentAcceptanceResponse"];

/**
 * The signed-in identity has no local account (ADR 0030 D3): the JWT
 * middleware answered 403 `reason: "not_provisioned"`. Thrown by the status
 * query so `AcceptanceGate` can replace the app with the no-access screen.
 */
export class NotProvisionedError extends Error {
  constructor() {
    super("This account is not part of the beta");
    this.name = "NotProvisionedError";
  }
}

function isNotProvisioned(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { reason?: unknown }).reason === "not_provisioned"
  );
}

/**
 * The signed-in user's legal-acceptance status (L1-U3, ADR 0028 D4/D5): per
 * document, the current required version and whether it has been accepted, plus
 * the app-wide `enforced` flag that drives the acceptance gate.
 *
 * Doubles as the SPA's earliest authenticated API probe: a 403
 * `not_provisioned` here (ADR 0030 D3) throws `NotProvisionedError`, which is
 * definitive (no retry) and switches the gate to the no-access screen.
 */
export function useLegalAcceptance(options?: { enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: LEGAL_ACCEPTANCE_QUERY_KEY,
    enabled: options?.enabled ?? true,
    retry: (failureCount, error) =>
      !(error instanceof NotProvisionedError) && failureCount < 3,
    queryFn: async () => {
      const { data, error } = await client.GET("/api/legal/acceptance");
      if (error !== undefined) {
        if (isNotProvisioned(error)) {
          throw new NotProvisionedError();
        }
        throw new Error("Failed to load legal acceptance status");
      }
      return data;
    },
  });
}

/**
 * Record acceptance of one legal document at its current version. The endpoint
 * returns the refreshed status, which is written straight into the query cache so
 * the gate re-evaluates without a second round trip.
 */
export function useAcceptLegalDocument() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { document: string; version: string }) => {
      const { data, error } = await client.POST("/api/legal/acceptance", { body: input });
      if (error !== undefined) {
        throw new Error("Failed to record acceptance");
      }
      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(LEGAL_ACCEPTANCE_QUERY_KEY, data);
    },
  });
}
